import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { showModal, hideModal } from '../../../../actions/ModalActions';
import DragHandle from '../../../common/ui/DragHandle';
import { getPhotos, editPhoto } from '../../../../actions/website/entities/PhotosActions';
import { moveEntity, resetEntities, addEntity } from '../../../../actions/website/EntityActions';
import { saveCrop, tempUploadPromise } from '../../../../actions/website/FileUploadActions';
import EntityHeader from './EntityHeader';
import FileInputButton from '../../../common/form/FileInputButton';
import { togglePage } from '../../../../actions/website/PageActions';
import { getImageUrl } from '../../../../cards/util/imageHelper';

const Photo = SortableElement(({ photo, onEdit, onDelete }) => {
  const noPropagate = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  return (
    <li className="sortable-item editable-entity photo-entity" onClick={() => onEdit(photo)}>
      <div className="col-sm-1">
        <DragHandle tooltip="Drag to reorder" />
      </div>
      <div className="col-sm-8">
        <div className="photo-preview" style={{ backgroundImage: `url(${getImageUrl(photo.image_id)}?w=100&h=100)` }} />
        <span className="photo-caption description">
          {photo.caption}
        </span>
      </div>
      <div className="col-sm-3 entity-actions" onClick={noPropagate}>
        <button onClick={() => onEdit(photo)} title="Edit Photo" className="btn btn-icon with-tooltip no-tooltip-on-mobile" data-tooltip="Edit"><i className="glyphicon glyphicon-pencil" /></button>
        <button onClick={() => onDelete(photo)} title="Delete Photo" className="btn btn-icon with-tooltip no-tooltip-on-mobile" data-tooltip="Delete"><i className="glyphicon glyphicon-trash" /></button>
      </div>
    </li>
  );
});

Photo.propTypes = {
  photo: PropTypes.shape({}).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number,
};


class Photos extends Component {
  componentDidMount() {
    const { getPhotoEntities } = this.props;
    getPhotoEntities();
  }

  componentWillReceiveProps(nextProps) {
    const { openCropModal, openSaveModal, closeModal } = this.props;
    if (nextProps.photoFilePreview.s3_key && (this.props.photoFilePreview.s3_key !== nextProps.photoFilePreview.s3_key)) {
      openCropModal(nextProps.photoFilePreview);
    } else if (nextProps.photoFilePreview.uuid && (this.props.photoFilePreview.uuid !== nextProps.photoFilePreview.uuid)) {
      closeModal();
      openSaveModal(nextProps.photoFilePreview);
    }
  }

  componentWillUnmount() {
    const { unloadEntities } = this.props;
    unloadEntities();
  }

  render() {
    const { photos, orderedIds, onEdit, onDelete, page, themeKey, onPhotoSelect, photoFilePreview, onPageToggle } = this.props;
    const photoCount = Object.keys(photos).length;
    const { env } = window.zola;
    let photoLimit;
    if (env === 'development') {
      photoLimit = 25;
    } else if (env === 'staging') {
      photoLimit = 25;
    } else {
      photoLimit = 100;
    }
    const nearPhotoLimit = (photoCount >= (photoLimit - 5) && photoCount < photoLimit);
    const atPhotoLimit = photoCount >= photoLimit;

    const button = (
      <div>
        <div className="upload-photo">
          <FileInputButton
            inputId="new_photo"
            onSelect={onPhotoSelect}
            types="image/*"
            btnClass="btn btn-primary margin-top margin-bottom"
            btnText={photoFilePreview.busy ? 'Uploading...' : '+ Add Photo'}
            busyOverride
            busy={photoFilePreview.busy}
            disabledOverride={atPhotoLimit}
          />
          {nearPhotoLimit && (
            <div className="photo-limit">
              <p>{photoCount}/{photoLimit} Photos Added</p>
            </div>
          )}
          {atPhotoLimit && (
            <div className="photo-limit">
              <p>Limit Reached: {photoLimit} Photos Added</p>
            </div>
          )}
        </div>
        &nbsp;
        <small>(max file size 5 MB)</small>
      </div>
    );

    return (
      <div>
        {themeKey && <EntityHeader entityOptions={button} page={page} themeKey={themeKey} onPageToggle={onPageToggle} />}
        <ul className="sortable-list">
          {photos && orderedIds.map((photoId, idx) => (
            <Photo key={idx} onDelete={onDelete} onEdit={onEdit} photo={photos[photoId]} index={idx} />
          ))}
        </ul>
      </div>
    );
  }
}

Photos.propTypes = {
  photos: PropTypes.shape({}),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  getPhotoEntities: PropTypes.func.isRequired,
  openCropModal: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  unloadEntities: PropTypes.func.isRequired,
  page: PropTypes.shape({}),
  themeKey: PropTypes.string,
  onPhotoSelect: PropTypes.func,
  photoFilePreview: PropTypes.shape({ uuid: PropTypes.string, s3_key: PropTypes.string }),
  onPageToggle: PropTypes.func,
  openSaveModal: PropTypes.func,
  closeModal: PropTypes.func
};


const mapStateToProps = state => ({
  photos: state.website.entities.byId,
  orderedIds: state.website.entities.orderedIds,
  page: state.website.pages.byId[state.website.pages.typeToId.PHOTO],
  themeKey: state.wedding.theme.key,
  photoFilePreview: state.website.entities.filePreview.new_photo || {}
});

const mapDispatchToProps = dispatch => ({
  onSortEnd: ({ oldIndex, newIndex }) => {
    dispatch(moveEntity(oldIndex, newIndex));
  },
  openCropModal: (image) => {
    dispatch(showModal('CROP_MODAL', {
      aspectRatio: 1,
      imageUrl: image.url,
      onCropSelect: (crop) => {
        dispatch(saveCrop(image.s3_bucket, image.s3_key, crop, image.content_type, 'new_photo'));
      },
      subcopy: "Don't want a white border? Drag the corners and crop your photo to a square."
    }));
  },
  openSaveModal: (image) => {
    dispatch(addEntity());
    dispatch(showModal('PHOTO', { newPhoto: image }, { size: 'md' }));
  },
  getPhotoEntities: () => {
    dispatch(getPhotos());
  },
  onEdit: (photo) => {
    dispatch(editPhoto(photo));
    dispatch(showModal('PHOTO', { isUpdate: true }, { size: 'md' }));
  },
  onDelete: (entity) => {
    dispatch(showModal('DELETE_ENTITY', { entity }));
  },
  unloadEntities: () => {
    dispatch(resetEntities());
  },
  onPhotoSelect: (file) => {
    dispatch(tempUploadPromise(file, 'new_photo'));
  },
  onPageToggle: pageId => dispatch(togglePage(pageId)),
  closeModal: () => dispatch(hideModal())
});

const SortablePhotos = SortableContainer(Photos);

export default connect(mapStateToProps, mapDispatchToProps)(SortablePhotos);



// WEBPACK FOOTER //
// ./src/components/manage/website/entities/Photos.jsx